import { useRef, useState } from "react";
import { Paperclip, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const MAX_TOTAL = 10 * 1024 * 1024;
const fmt = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const AttachmentsField = ({ value, onChange }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const total = value.reduce((s, a) => s + (a.size || 0), 0);

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        let running = total;
        const added = [];
        setUploading(true);
        for (const file of files) {
            if (running + file.size > MAX_TOTAL) { toast.error(`„${file.name}” – przekroczono łączny limit 10 MB.`); continue; }
            try {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                added.push({ url: data.url, filename: data.original_filename || file.name, size: data.size || file.size });
                running += file.size;
            } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Nie udało się wysłać „${file.name}”.`);
            }
        }
        setUploading(false);
        if (added.length) onChange([...value, ...added]);
    };

    return (
        <div data-testid="mailing-attachments">
            <div className="flex items-center gap-3 flex-wrap">
                <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="mailing-attachments-add"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-dashed border-[#0D4F3C]/40 text-[#0D4F3C] text-sm hover:bg-[#0D4F3C]/5 disabled:opacity-50">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} Dodaj załącznik
                </button>
                <span className={`text-xs ${total > MAX_TOTAL * 0.9 ? "text-red-600" : "text-gray-500"}`} data-testid="mailing-attachments-total">
                    {fmt(total)} / 10 MB · PDF, obrazy, DOC
                </span>
                <input ref={inputRef} type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.odt,.rtf" onChange={onFiles} />
            </div>
            {value.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                    {value.map((a, i) => (
                        <li key={a.url} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-[#FAF8F3] text-sm" data-testid={`mailing-attachment-${i}`}>
                            <span className="truncate text-[#092E22]">{a.filename}</span>
                            <span className="flex items-center gap-2 shrink-0">
                                <span className="text-xs text-gray-400">{fmt(a.size || 0)}</span>
                                <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700" data-testid={`mailing-attachment-remove-${i}`}>
                                    <X size={14} />
                                </button>
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AttachmentsField;
